import React, { useState, useEffect } from 'react';
import { adminServiceFactory } from '../../Services/AdminService';
import { Table, Button, Form, Alert, Modal, Card, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { 
  FileText, 
  Plus,
  Pencil,
  Trash,
  PlusCircle
} from 'react-bootstrap-icons';
import { Timestamp } from 'firebase/firestore';
import './Admin.css';

const AdminReportsManagement = () => {
  // Get ReportService instance
  const reportService = adminServiceFactory.reportService;
  
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Report Modal State
  const [showReportModal, setShowReportModal] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportFormData, setReportFormData] = useState({
    symbol: "",
    companyName: "",
    sector: "",
    reportId: "",
    reportTitle: "",
    allowedPlans: [],
    minRiskLevel: 1,
    isPremium: false,
    recommendations: [],
    reportStatus: "ACTIVE",
    tags: [],
    categories: [],
    // Report-level price fields (default to first recommendation)
    triggerPrice: "",
    targetPrice: "",
    stopLoss: "",
    buyingRange: ""
  });

  // Recommendation Modal State
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [currentRecommendation, setCurrentRecommendation] = useState(null);
  const [recommendationFormData, setRecommendationFormData] = useState({
    action: "BUY",
    recommendationType: "INITIAL",
    currentPrice: "",
    targetPrice: "",
    stopLoss: "",
    buyingRange: "",
    potentialUpside: "",
    potentialRisk: "",
    riskRewardRatio: "",
    positionSize: "",
    quantity: "",
    investmentAmount: "",
    validityPeriod: 90,
    reasoning: "",
    keyFactors: [],
    marketCondition: "BULLISH",
    sectorOutlook: "POSITIVE"
  });

  // Recommendations Table State
  const [showRecommendationsTable, setShowRecommendationsTable] = useState(false);
  const [selectedReportForRecommendations, setSelectedReportForRecommendations] = useState(null);

  const availablePlans = ["Prime", "Prime+", "Elite", "Elite+", "Supreme", "Supreme+"];
  const availableActions = ["BUY", "SELL", "HOLD", "AVERAGE", "EXIT_PARTIAL", "EXIT_FULL", "PYRAMID"];
  const availableTypes = ["INITIAL", "FOLLOW_UP", "EXIT", "AVERAGE_DOWN", "AVERAGE_UP"];
  const marketConditions = ["BULLISH", "BEARISH", "SIDEWAYS"];
  const sectorOutlooks = ["POSITIVE", "NEGATIVE", "NEUTRAL"];

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const result = await reportService.getAllReports();
      if (result.success) {
        setReports(result.data);
      } else {
        setError(result.message || 'Failed to load reports');
      }
    } catch (err) {
      console.error("Error fetching reports: ", err);
      setError("Failed to load reports.");
    } finally {
      setLoadingReports(false);
    }
  };

  const handleReportFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setReportFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleShowReportModal = (report = null) => {
    if (report) {
      setCurrentReport(report);
      
      // If report doesn't have price fields but has recommendations, populate from first recommendation
      let triggerPrice = report.triggerPrice || "";
      let targetPrice = report.targetPrice || "";
      let stopLoss = report.stopLoss || "";
      let buyingRange = report.buyingRange || "";
      
      if (!triggerPrice && report.recommendations && report.recommendations.length > 0) {
        const firstRec = report.recommendations[0];
        triggerPrice = firstRec.currentPrice || "";
        targetPrice = firstRec.targetPrice || "";
        stopLoss = firstRec.stopLoss || "";
        buyingRange = firstRec.buyingRange || "";
      }
      
      setReportFormData({
        symbol: report.symbol || "",
        companyName: report.companyName || "",
        sector: report.sector || "",
        reportId: report.reportId || "",
        reportTitle: report.reportTitle || "",
        allowedPlans: report.allowedPlans || [],
        minRiskLevel: report.minRiskLevel || 1,
        isPremium: report.isPremium || false,
        recommendations: report.recommendations || [],
        reportStatus: report.reportStatus || "ACTIVE",
        tags: report.tags || [],
        categories: report.categories || [],
        // Report-level price fields
        triggerPrice: triggerPrice,
        targetPrice: targetPrice,
        stopLoss: stopLoss,
        buyingRange: buyingRange
      });
    } else {
      setCurrentReport(null);
      setReportFormData({
        symbol: "",
        companyName: "",
        sector: "",
        reportId: "",
        reportTitle: "",
        allowedPlans: [],
        minRiskLevel: 1,
        isPremium: false,
        recommendations: [],
        reportStatus: "ACTIVE",
        tags: [],
        categories: [],
        // Report-level price fields
        triggerPrice: "",
        targetPrice: "",
        stopLoss: "",
        buyingRange: ""
      });
    }
    setShowReportModal(true);
  };

  const handleCloseReportModal = () => {
    setShowReportModal(false);
    setCurrentReport(null);
    setReportFormData({
      symbol: "",
      companyName: "",
      sector: "",
      reportId: "",
      reportTitle: "",
      allowedPlans: [],
      minRiskLevel: 1,
      isPremium: false,
      recommendations: [],
      reportStatus: "ACTIVE",
      tags: [],
      categories: [],
      // Report-level price fields
      triggerPrice: "",
      targetPrice: "",
      stopLoss: "",
      buyingRange: ""
    });
    setError("");
  };

  const handleSaveReport = async () => {
    try {
      setError("");
      
      if (!reportFormData.symbol || !reportFormData.companyName || !reportFormData.reportTitle) {
        setError("Symbol, Company Name, and Report Title are required.");
        return;
      }

      if (currentReport) {
        // Update existing report
        const result = await reportService.updateReport(currentReport.id, reportFormData, 'admin');
        if (result.success) {
          const updatedReports = reports.map(report =>
            report.id === currentReport.id ? result.data : report
          );
          setReports(updatedReports);
          setSuccess("Report updated successfully!");
        } else {
          setError(result.message || 'Failed to update report');
          return;
        }
      } else {
        // Create new report - we need an initial recommendation
        if (!recommendationFormData.currentPrice || !recommendationFormData.targetPrice) {
          setError("Please add an initial recommendation with price details.");
          return;
        }
        
        const result = await reportService.createReport(reportFormData, recommendationFormData, 'admin');
        if (result.success) {
          setReports(prev => [...prev, result.data]);
          setSuccess("Report created successfully!");
        } else {
          setError(result.message || 'Failed to create report');
          return;
        }
      }
      
      handleCloseReportModal();
      setTimeout(() => setSuccess(""), 3000);
      
    } catch (err) {
      console.error("Error saving report: ", err);
      setError("Failed to save report. Please try again.");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm("Are you sure you want to delete this report? This action cannot be undone.")) {
      try {
        const result = await reportService.deleteReport(reportId, 'admin');
        if (result.success) {
          setReports(prev => prev.filter(report => report.id !== reportId));
          setSuccess("Report deleted successfully!");
          setTimeout(() => setSuccess(""), 3000);
        } else {
          setError(result.message || 'Failed to delete report');
        }
      } catch (err) {
        console.error("Error deleting report: ", err);
        setError("Failed to delete report. Please try again.");
      }
    }
  };

  const handlePlanSelection = (plan) => {
    setReportFormData(prev => ({
      ...prev,
      allowedPlans: prev.allowedPlans.includes(plan)
        ? prev.allowedPlans.filter(p => p !== plan)
        : [...prev.allowedPlans, plan]
    }));
  };

  // ===== RECOMMENDATION MANAGEMENT =====

  const handleShowRecommendationModal = (recommendation = null, reportId = null) => {
    if (recommendation) {
      setCurrentRecommendation(recommendation);
      setRecommendationFormData({
        action: recommendation.action || "BUY",
        recommendationType: recommendation.recommendationType || "INITIAL",
        currentPrice: recommendation.currentPrice || "",
        targetPrice: recommendation.targetPrice || "",
        stopLoss: recommendation.stopLoss || "",
        buyingRange: recommendation.buyingRange || "",
        potentialUpside: recommendation.potentialUpside || "",
        potentialRisk: recommendation.potentialRisk || "",
        riskRewardRatio: recommendation.riskRewardRatio || "",
        positionSize: recommendation.positionSize || "",
        quantity: recommendation.quantity || "",
        investmentAmount: recommendation.investmentAmount || "",
        validityPeriod: recommendation.validityPeriod || 90,
        reasoning: recommendation.reasoning || "",
        keyFactors: recommendation.keyFactors || [],
        marketCondition: recommendation.marketCondition || "BULLISH",
        sectorOutlook: recommendation.sectorOutlook || "POSITIVE"
      });
    } else {
      setCurrentRecommendation(null);
      setRecommendationFormData({
        action: "BUY",
        recommendationType: "INITIAL",
        currentPrice: "",
        targetPrice: "",
        stopLoss: "",
        buyingRange: "",
        potentialUpside: "",
        potentialRisk: "",
        riskRewardRatio: "",
        positionSize: "",
        quantity: "",
        investmentAmount: "",
        validityPeriod: 90,
        reasoning: "",
        keyFactors: [],
        marketCondition: "BULLISH",
        sectorOutlook: "POSITIVE"
      });
    }
    setShowRecommendationModal(true);
  };

  const handleCloseRecommendationModal = () => {
    setShowRecommendationModal(false);
    setCurrentRecommendation(null);
    setRecommendationFormData({
      action: "BUY",
      recommendationType: "INITIAL",
      currentPrice: "",
      targetPrice: "",
      stopLoss: "",
      buyingRange: "",
      potentialUpside: "",
      potentialRisk: "",
      riskRewardRatio: "",
      positionSize: "",
      quantity: "",
      investmentAmount: "",
      validityPeriod: 90,
      reasoning: "",
      keyFactors: [],
      marketCondition: "BULLISH",
      sectorOutlook: "POSITIVE"
    });
    setError("");
  };

  const handleRecommendationFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setRecommendationFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Sync report-level price fields if this is the first recommendation
    if (!currentReport && ['currentPrice', 'targetPrice', 'stopLoss', 'buyingRange'].includes(name)) {
      const updatedData = {
        ...recommendationFormData,
        [name]: type === 'checkbox' ? checked : value
      };
      syncReportPriceFields(updatedData);
    }
  };

  // Auto-calculate risk metrics when prices change
  const calculateRiskMetrics = (currentPrice, targetPrice, stopLoss) => {
    if (!currentPrice || !targetPrice || !stopLoss) return {};
    
    const current = parseFloat(currentPrice);
    const target = parseFloat(targetPrice);
    const stop = parseFloat(stopLoss);
    
    if (isNaN(current) || isNaN(target) || isNaN(stop)) return {};
    
    // Calculate potential upside (percentage)
    const upside = ((target - current) / current) * 100;
    
    // Calculate potential risk (percentage)
    const risk = ((current - stop) / current) * 100;
    
    // Calculate risk-reward ratio
    const riskRewardRatio = risk > 0 ? Math.abs(upside / risk) : 0;
    
    return {
      potentialUpside: upside.toFixed(2),
      potentialRisk: risk.toFixed(2),
      riskRewardRatio: riskRewardRatio.toFixed(2)
    };
  };

  const handlePriceChange = (event) => {
    const { name, value } = event.target;
    
    // Update the form data
    setRecommendationFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-calculate risk metrics if all three price fields are filled
    const { currentPrice, targetPrice, stopLoss } = {
      ...recommendationFormData,
      [name]: value
    };
    
    if (currentPrice && targetPrice && stopLoss) {
      const metrics = calculateRiskMetrics(currentPrice, targetPrice, stopLoss);
      setRecommendationFormData(prev => ({
        ...prev,
        ...metrics
      }));
    }
    
    // Sync report-level price fields if this is the first recommendation
    if (!currentReport && ['currentPrice', 'targetPrice', 'stopLoss', 'buyingRange'].includes(name)) {
      const updatedData = {
        ...recommendationFormData,
        [name]: value
      };
      syncReportPriceFields(updatedData);
    }
  };

  // Sync report-level price fields with first recommendation
  const syncReportPriceFields = (recommendationData) => {
    setReportFormData(prev => ({
      ...prev,
      triggerPrice: recommendationData.currentPrice || "",
      targetPrice: recommendationData.targetPrice || "",
      stopLoss: recommendationData.stopLoss || "",
      buyingRange: recommendationData.buyingRange || ""
    }));
  };

  const handleSaveRecommendation = async () => {
    try {
      setError("");
      
      if (!recommendationFormData.currentPrice || !recommendationFormData.targetPrice || !recommendationFormData.stopLoss) {
        setError("Current Price, Target Price, and Stop Loss are required.");
        return;
      }

      if (currentRecommendation) {
        // Update existing recommendation
        const result = await reportService.updateRecommendation(
          currentReport.id, 
          currentRecommendation.recommendationId, 
          recommendationFormData, 
          'admin'
        );
        if (result.success) {
          const updatedReports = reports.map(report =>
            report.id === currentReport.id ? result.data : report
          );
          setReports(updatedReports);
          setSuccess("Recommendation updated successfully!");
        } else {
          setError(result.message || 'Failed to update recommendation');
          return;
        }
      } else {
        // Add new recommendation
        const result = await reportService.addRecommendation(
          currentReport.id, 
          recommendationFormData, 
          'admin'
        );
        if (result.success) {
          const updatedReports = reports.map(report =>
            report.id === currentReport.id ? result.data : report
          );
          setReports(updatedReports);
          setSuccess("Recommendation added successfully!");
        } else {
          setError(result.message || 'Failed to add recommendation');
          return;
        }
      }
      
      handleCloseRecommendationModal();
      setTimeout(() => setSuccess(""), 3000);
      
    } catch (err) {
      console.error("Error saving recommendation: ", err);
      setError("Failed to save recommendation. Please try again.");
    }
  };

  const handleDeleteRecommendation = async (reportId, recommendationId) => {
    if (window.confirm("Are you sure you want to delete this recommendation? This action cannot be undone.")) {
      try {
        const result = await reportService.deleteRecommendation(reportId, recommendationId, 'admin');
        if (result.success) {
          const updatedReports = reports.map(report =>
            report.id === reportId ? result.data : report
          );
          setReports(updatedReports);
          setSuccess("Recommendation deleted successfully!");
          setTimeout(() => setSuccess(""), 3000);
        } else {
          setError(result.message || 'Failed to delete recommendation');
        }
      } catch (err) {
        console.error("Error deleting recommendation: ", err);
        setError("Failed to delete recommendation. Please try again.");
      }
    }
  };

  const handleShowRecommendationsTable = (report) => {
    setSelectedReportForRecommendations(report);
    setShowRecommendationsTable(true);
  };

  const handleCloseRecommendationsTable = () => {
    setShowRecommendationsTable(false);
    setSelectedReportForRecommendations(null);
  };

  return (
    <div className="admin-section">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">
          <FileText className="me-2" />
          Report Management
        </h2>
        <Button variant="primary" onClick={() => handleShowReportModal()}>
          <Plus size={16} className="me-1" />
          Create New Report
        </Button>
      </div>

      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      <Card>
        <Card.Body>
          {loadingReports ? (
            <div className="text-center"><Spinner animation="border" /></div>
          ) : (
            <Table responsive striped bordered hover className="report-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Symbol</th>
                  <th>Trigger Price</th>
                  <th>Target Price</th>
                  <th>Stop Loss</th>
                  <th>Buying Range</th>
                  <th>Allowed Plans</th>
                  <th>Recommendations</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id}>
                    <td>
                      <div className="action-buttons">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleShowReportModal(report)}
                          className="me-1"
                        >
                          <Pencil size={14} className="me-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteReport(report.id)}
                        >
                          <Trash size={14} className="me-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                    <td><strong>{report.symbol || 'N/A'}</strong></td>
                    <td>
                      {report.triggerPrice ? (
                        <span className="fw-bold text-primary">₹{report.triggerPrice}</span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      {report.targetPrice ? (
                        <span className="fw-bold text-success">₹{report.targetPrice}</span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      {report.stopLoss ? (
                        <span className="fw-bold text-danger">₹{report.stopLoss}</span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      {report.buyingRange ? (
                        <span className="fw-bold text-info">{report.buyingRange}</span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      <div className="plan-badges">
                        {report.allowedPlans && report.allowedPlans.length > 0 ? (
                          report.allowedPlans.map((plan, index) => (
                            <span key={index} className="badge bg-primary me-1 mb-1">
                              {plan}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted">No plans</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="recommendation-info">
                        {report.recommendations && report.recommendations.length > 0 ? (
                          <div>
                            <span className="badge bg-success me-1">
                              {report.recommendations.length} total
                            </span>
                            <span className="badge bg-warning me-1">
                              {report.recommendations.filter(r => r.status === 'ACTIVE').length} active
                            </span>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleShowRecommendationsTable(report)}
                              className="me-1"
                            >
                              View All
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowRecommendationModal(null, report.id)}
                            >
                              <PlusCircle size={12} className="me-1" />
                              Add
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <span className="text-muted me-2">No recommendations</span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowRecommendationModal(null, report.id)}
                            >
                              <PlusCircle size={12} className="me-1" />
                              Add First
                            </Button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge bg-${report.reportStatus === 'ACTIVE' ? 'success' : report.reportStatus === 'CLOSED' ? 'secondary' : 'warning'}`}>
                        {report.reportStatus || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {report.createdAt instanceof Timestamp
                        ? report.createdAt.toDate().toLocaleDateString('en-GB')
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {reports.length === 0 && !loadingReports && <p className="text-muted text-center">No reports found.</p>}
        </Card.Body>
      </Card>

      {/* Report Modal */}
      <Modal show={showReportModal} onHide={handleCloseReportModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentReport ? 'Edit Report' : 'Create New Report'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Symbol *</Form.Label>
                  <Form.Control
                    type="text"
                    name="symbol"
                    value={reportFormData.symbol}
                    onChange={handleReportFormChange}
                    placeholder="e.g., RELIANCE"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="companyName"
                    value={reportFormData.companyName}
                    onChange={handleReportFormChange}
                    placeholder="e.g., Reliance Industries Ltd"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sector</Form.Label>
                  <Form.Control
                    type="text"
                    name="sector"
                    value={reportFormData.sector}
                    onChange={handleReportFormChange}
                    placeholder="e.g., Oil & Gas"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Report ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="reportId"
                    value={reportFormData.reportId}
                    onChange={handleReportFormChange}
                    placeholder="e.g., RELIANCE-Q3-2024"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Report Title *</Form.Label>
              <Form.Control
                type="text"
                name="reportTitle"
                value={reportFormData.reportTitle}
                onChange={handleReportFormChange}
                placeholder="e.g., Reliance Q3 2024 Analysis - Strong Growth Outlook"
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Risk Level</Form.Label>
                  <Form.Select
                    name="minRiskLevel"
                    value={reportFormData.minRiskLevel}
                    onChange={handleReportFormChange}
                  >
                    {[1, 2, 3, 4, 5].map(level => (
                      <option key={level} value={level}>Level {level}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  name="isPremium"
                  label="Premium Content"
                  checked={reportFormData.isPremium}
                  onChange={handleReportFormChange}
                />
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Allowed Plans</Form.Label>
              <div className="plan-checkboxes">
                {availablePlans.map(plan => (
                  <Form.Check
                    key={plan}
                    type="checkbox"
                    id={`plan-${plan}`}
                    label={plan}
                    checked={reportFormData.allowedPlans.includes(plan)}
                    onChange={() => handlePlanSelection(plan)}
                    inline
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Report Status</Form.Label>
              <Form.Select
                name="reportStatus"
                value={reportFormData.reportStatus}
                onChange={handleReportFormChange}
              >
                <option value="ACTIVE">Active</option>
                <option value="CLOSED">Closed</option>
                <option value="ARCHIVED">Archived</option>
              </Form.Select>
            </Form.Group>

            {/* Report-level Price Fields */}
            <hr className="my-4" />
            <h6 className="mb-3">Report Price Overview</h6>
            <p className="text-muted small mb-3">These fields will default to the first recommendation's values and can be customized for the overall report.</p>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Trigger Price (Current Price)</Form.Label>
                  <Form.Control
                    type="number"
                    name="triggerPrice"
                    value={reportFormData.triggerPrice}
                    onChange={handleReportFormChange}
                    placeholder="Will sync from first recommendation"
                    step="0.01"
                  />
                  <Form.Text className="text-muted">
                    Defaults to the first recommendation's current price
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Price</Form.Label>
                  <Form.Control
                    type="number"
                    name="targetPrice"
                    value={reportFormData.targetPrice}
                    onChange={handleReportFormChange}
                    placeholder="Will sync from first recommendation"
                    step="0.01"
                  />
                  <Form.Text className="text-muted">
                    Defaults to the first recommendation's target price
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Stop Loss</Form.Label>
                  <Form.Control
                    type="number"
                    name="stopLoss"
                    value={reportFormData.stopLoss}
                    onChange={handleReportFormChange}
                    placeholder="Will sync from first recommendation"
                    step="0.01"
                  />
                  <Form.Text className="text-muted">
                    Defaults to the first recommendation's stop loss
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Buying Range</Form.Label>
                  <Form.Control
                    type="text"
                    name="buyingRange"
                    value={reportFormData.buyingRange}
                    onChange={handleReportFormChange}
                    placeholder="Will sync from first recommendation"
                  />
                  <Form.Text className="text-muted">
                    Defaults to the first recommendation's buying range
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Initial Recommendation Section - Only show when creating new report */}
            {!currentReport && (
              <>
                <hr className="my-4" />
                <h6 className="mb-3">Initial Recommendation (Required)</h6>
                <p className="text-muted small mb-3">Every new report must include an initial recommendation.</p>
                
                <Alert variant="info" className="mb-3">
                  <strong>💡 Auto-Calculation Feature:</strong> Fill in Current Price, Target Price, and Stop Loss to automatically calculate Potential Upside, Potential Risk, and Risk-Reward Ratio. You can still edit these calculated values if needed.
                </Alert>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Action *</Form.Label>
                      <Form.Select
                        name="action"
                        value={recommendationFormData.action}
                        onChange={handleRecommendationFormChange}
                        required
                      >
                        {availableActions.map(action => (
                          <option key={action} value={action}>{action}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Recommendation Type *</Form.Label>
                      <Form.Select
                        name="recommendationType"
                        value={recommendationFormData.recommendationType}
                        onChange={handleRecommendationFormChange}
                        required
                      >
                        {availableTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Price *</Form.Label>
                      <Form.Control
                        type="number"
                        name="currentPrice"
                        value={recommendationFormData.currentPrice}
                        onChange={handlePriceChange}
                        placeholder="e.g., 2500"
                        step="0.01"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Target Price *</Form.Label>
                      <Form.Control
                        type="number"
                        name="targetPrice"
                        value={recommendationFormData.targetPrice}
                        onChange={handlePriceChange}
                        placeholder="e.g., 3000"
                        step="0.01"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stop Loss *</Form.Label>
                      <Form.Control
                        type="number"
                        name="stopLoss"
                        value={recommendationFormData.stopLoss}
                        onChange={handlePriceChange}
                        placeholder="e.g., 2300"
                        step="0.01"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Buying Range</Form.Label>
                      <Form.Control
                        type="text"
                        name="buyingRange"
                        value={recommendationFormData.buyingRange}
                        placeholder="e.g., 2400-2600"
                        onChange={handleRecommendationFormChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Validity Period (days)</Form.Label>
                      <Form.Control
                        type="number"
                        name="validityPeriod"
                        value={recommendationFormData.validityPeriod}
                        onChange={handleRecommendationFormChange}
                        placeholder="90"
                        min="1"
                        max="365"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 auto-calculated-field">
                      <Form.Label>
                        Potential Upside (%) 
                        <span className="text-muted ms-1">(Auto-calculated)</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="potentialUpside"
                        value={recommendationFormData.potentialUpside}
                        onChange={handleRecommendationFormChange}
                        placeholder="Auto-calculated"
                        step="0.1"
                        className={recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss ? 'border-success' : ''}
                      />
                      <Form.Text className="text-muted">
                        {recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss 
                          ? 'Calculated from Current Price and Target Price' 
                          : 'Fill in Current Price, Target Price, and Stop Loss to auto-calculate'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3 auto-calculated-field">
                      <Form.Label>
                        Potential Risk (%) 
                        <span className="text-muted ms-1">(Auto-calculated)</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="potentialRisk"
                        value={recommendationFormData.potentialRisk}
                        onChange={handleRecommendationFormChange}
                        placeholder="Auto-calculated"
                        step="0.1"
                        className={recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss ? 'border-success' : ''}
                      />
                      <Form.Text className="text-muted">
                        {recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss 
                          ? 'Calculated from Current Price and Stop Loss' 
                          : 'Fill in Current Price, Target Price, and Stop Loss to auto-calculate'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 auto-calculated-field">
                      <Form.Label>
                        Risk-Reward Ratio 
                        <span className="text-muted ms-1">(Auto-calculated)</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="riskRewardRatio"
                        value={recommendationFormData.riskRewardRatio}
                        onChange={handleRecommendationFormChange}
                        placeholder="Auto-calculated"
                        step="0.1"
                        className={recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss ? 'border-success' : ''}
                      />
                      <Form.Text className="text-muted">
                        {recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss 
                          ? 'Calculated from Potential Upside and Risk' 
                          : 'Fill in Current Price, Target Price, and Stop Loss to auto-calculate'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Position Size (%)</Form.Label>
                      <Form.Control
                        type="number"
                        name="positionSize"
                        value={recommendationFormData.positionSize}
                        onChange={handleRecommendationFormChange}
                        placeholder="e.g., 5"
                        step="0.1"
                        max="100"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        name="quantity"
                        value={recommendationFormData.quantity}
                        onChange={handleRecommendationFormChange}
                        placeholder="e.g., 100"
                        min="1"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Investment Amount (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        name="investmentAmount"
                        value={recommendationFormData.investmentAmount}
                        onChange={handleRecommendationFormChange}
                        placeholder="e.g., 250000"
                        step="1000"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Market Condition</Form.Label>
                      <Form.Select
                        name="marketCondition"
                        value={recommendationFormData.marketCondition}
                        onChange={handleRecommendationFormChange}
                      >
                        {marketConditions.map(condition => (
                          <option key={condition} value={condition}>{condition}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sector Outlook</Form.Label>
                      <Form.Select
                        name="sectorOutlook"
                        value={recommendationFormData.sectorOutlook}
                        onChange={handleRecommendationFormChange}
                      >
                        {sectorOutlooks.map(outlook => (
                          <option key={outlook} value={outlook}>{outlook}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Reasoning</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="reasoning"
                    value={recommendationFormData.reasoning}
                    onChange={handleRecommendationFormChange}
                    placeholder="Provide detailed reasoning for this recommendation..."
                  />
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseReportModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveReport}>
            {currentReport ? 'Update Report' : 'Create Report'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Recommendation Modal */}
      <Modal show={showRecommendationModal} onHide={handleCloseRecommendationModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentRecommendation ? 'Edit Recommendation' : 'Add New Recommendation'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>💡 Auto-Calculation Feature:</strong> Fill in Current Price, Target Price, and Stop Loss to automatically calculate Potential Upside, Potential Risk, and Risk-Reward Ratio. You can still edit these calculated values if needed.
          </Alert>
          
          <Form className="recommendation-form">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Action *</Form.Label>
                  <Form.Select
                    name="action"
                    value={recommendationFormData.action}
                    onChange={handleRecommendationFormChange}
                    required
                  >
                    {availableActions.map(action => (
                      <option key={action} value={action}>{action}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Recommendation Type *</Form.Label>
                  <Form.Select
                    name="recommendationType"
                    value={recommendationFormData.recommendationType}
                    onChange={handleRecommendationFormChange}
                    required
                  >
                    {availableTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Price *</Form.Label>
                  <Form.Control
                    type="number"
                    name="currentPrice"
                    value={recommendationFormData.currentPrice}
                    onChange={handlePriceChange}
                    placeholder="e.g., 2500"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Price *</Form.Label>
                  <Form.Control
                    type="number"
                    name="targetPrice"
                    value={recommendationFormData.targetPrice}
                    onChange={handlePriceChange}
                    placeholder="e.g., 3000"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Stop Loss *</Form.Label>
                  <Form.Control
                    type="number"
                    name="stopLoss"
                    value={recommendationFormData.stopLoss}
                    onChange={handlePriceChange}
                    placeholder="e.g., 2300"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Buying Range</Form.Label>
                  <Form.Control
                    type="text"
                    name="buyingRange"
                    value={recommendationFormData.buyingRange}
                    onChange={handleRecommendationFormChange}
                    placeholder="e.g., 2400-2600"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Validity Period (days)</Form.Label>
                  <Form.Control
                    type="number"
                    name="validityPeriod"
                    value={recommendationFormData.validityPeriod}
                    onChange={handleRecommendationFormChange}
                    placeholder="90"
                    min="1"
                    max="365"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 auto-calculated-field">
                  <Form.Label>
                    Potential Upside (%) 
                    <span className="text-muted ms-1">(Auto-calculated)</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="potentialUpside"
                    value={recommendationFormData.potentialUpside}
                    onChange={handleRecommendationFormChange}
                    placeholder="Auto-calculated"
                    step="0.1"
                    className={recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss ? 'border-success' : ''}
                  />
                  <Form.Text className="text-muted">
                    {recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss 
                      ? 'Calculated from Current Price and Target Price' 
                      : 'Fill in Current Price, Target Price, and Stop Loss to auto-calculate'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3 auto-calculated-field">
                  <Form.Label>
                    Potential Risk (%) 
                    <span className="text-muted ms-1">(Auto-calculated)</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="potentialRisk"
                    value={recommendationFormData.potentialRisk}
                    onChange={handleRecommendationFormChange}
                    placeholder="Auto-calculated"
                    step="0.1"
                    className={recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss ? 'border-success' : ''}
                  />
                  <Form.Text className="text-muted">
                    {recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss 
                      ? 'Calculated from Current Price and Stop Loss' 
                      : 'Fill in Current Price, Target Price, and Stop Loss to auto-calculate'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 auto-calculated-field">
                  <Form.Label>
                    Risk-Reward Ratio 
                    <span className="text-muted ms-1">(Auto-calculated)</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="riskRewardRatio"
                    value={recommendationFormData.riskRewardRatio}
                    onChange={handleRecommendationFormChange}
                    placeholder="Auto-calculated"
                    step="0.1"
                    className={recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss ? 'border-success' : ''}
                  />
                  <Form.Text className="text-muted">
                    {recommendationFormData.currentPrice && recommendationFormData.targetPrice && recommendationFormData.stopLoss 
                      ? 'Calculated from Potential Upside and Risk' 
                      : 'Fill in Current Price, Target Price, and Stop Loss to auto-calculate'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Position Size (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="positionSize"
                    value={recommendationFormData.positionSize}
                    onChange={handleRecommendationFormChange}
                    placeholder="e.g., 5"
                    step="0.1"
                    max="100"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    name="quantity"
                    value={recommendationFormData.quantity}
                    onChange={handleRecommendationFormChange}
                    placeholder="e.g., 100"
                    min="1"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Investment Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    name="investmentAmount"
                    value={recommendationFormData.investmentAmount}
                    onChange={handleRecommendationFormChange}
                    placeholder="e.g., 250000"
                    step="1000"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Market Condition</Form.Label>
                  <Form.Select
                    name="marketCondition"
                    value={recommendationFormData.marketCondition}
                    onChange={handleRecommendationFormChange}
                  >
                    {marketConditions.map(condition => (
                      <option key={condition} value={condition}>{condition}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sector Outlook</Form.Label>
                  <Form.Select
                    name="sectorOutlook"
                    value={recommendationFormData.sectorOutlook}
                    onChange={handleRecommendationFormChange}
                  >
                    {sectorOutlooks.map(outlook => (
                      <option key={outlook} value={outlook}>{outlook}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Reasoning</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="reasoning"
                value={recommendationFormData.reasoning}
                onChange={handleRecommendationFormChange}
                placeholder="Provide detailed reasoning for this recommendation..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRecommendationModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveRecommendation}>
            {currentRecommendation ? 'Update Recommendation' : 'Add Recommendation'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Recommendations Table Modal */}
      <Modal show={showRecommendationsTable} onHide={handleCloseRecommendationsTable} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Recommendations for {selectedReportForRecommendations?.symbol} - {selectedReportForRecommendations?.reportTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReportForRecommendations && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Total: {selectedReportForRecommendations.recommendations?.length || 0} recommendations</h6>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    handleCloseRecommendationsTable();
                    handleShowRecommendationModal(null, selectedReportForRecommendations.id);
                  }}
                >
                  <PlusCircle size={14} className="me-1" />
                  Add New
                </Button>
              </div>
              
              {selectedReportForRecommendations.recommendations && selectedReportForRecommendations.recommendations.length > 0 ? (
                <Table responsive striped bordered hover className="recommendations-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Type</th>
                      <th>Current Price</th>
                      <th>Target Price</th>
                      <th>Stop Loss</th>
                      <th>Upside %</th>
                      <th>Risk %</th>
                      <th>R/R Ratio</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReportForRecommendations.recommendations.map((rec, index) => (
                      <tr key={rec.recommendationId || index}>
                        <td>
                          <span className={`badge bg-${
                            rec.action === 'BUY' ? 'success' : 
                            rec.action === 'SELL' ? 'danger' : 
                            rec.action === 'HOLD' ? 'warning' : 'secondary'
                          }`}>
                            {rec.action}
                          </span>
                        </td>
                        <td>{rec.recommendationType}</td>
                        <td>₹{rec.currentPrice}</td>
                        <td>₹{rec.targetPrice}</td>
                        <td>₹{rec.stopLoss}</td>
                        <td>{rec.potentialUpside}%</td>
                        <td>{rec.potentialRisk}%</td>
                        <td>{rec.riskRewardRatio}</td>
                        <td>
                          <span className={`badge bg-${
                            rec.status === 'ACTIVE' ? 'success' : 
                            rec.status === 'CLOSED' ? 'secondary' : 'warning'
                          }`}>
                            {rec.status || 'ACTIVE'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                handleCloseRecommendationsTable();
                                handleShowRecommendationModal(rec, selectedReportForRecommendations.id);
                              }}
                              className="me-1"
                            >
                              <Pencil size={12} className="me-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteRecommendation(selectedReportForRecommendations.id, rec.recommendationId)}
                            >
                              <Trash size={12} className="me-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted text-center">No recommendations found for this report.</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRecommendationsTable}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminReportsManagement; 